import React, { useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppWidget from '../components/WhatsAppWidget';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { BadgePercent, Package, Headphones, TrendingUp, GraduationCap, Megaphone, Send } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BENEFITS = [
  { icon: BadgePercent, title: 'High Margins', desc: 'Industry-leading dealer margins across all four robot categories.' },
  { icon: Package, title: 'Demo Units', desc: 'Discounted demo units and display kits for your showroom.' },
  { icon: GraduationCap, title: 'Full Training', desc: 'Product, installation and after-sales training for your team.' },
  { icon: Megaphone, title: 'Marketing Support', desc: 'Brochures, creatives and lead sharing in your territory.' },
  { icon: Headphones, title: 'Dedicated Manager', desc: 'A dealer success manager for priority support and spare parts.' },
  { icon: TrendingUp, title: 'Growing Market', desc: 'Home robotics in India is growing 30%+ year on year.' },
];

export default function DealerPage() {
  const { toast } = useToast();
  const [scrolled, setScrolled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', city: '', category: '', message: '' });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    window.scrollTo(0, 0);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.category) {
      toast({ title: 'Error', description: 'Please select a product category of interest.', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        inquiry_type: 'partnership',
        message: `DEALER REGISTRATION REQUEST\nCompany: ${form.company}\nCity: ${form.city}\nCategory Interest: ${form.category}\n\n${form.message}`,
      });
      toast({ title: 'Application Received!', description: 'Our partnerships team will contact you within 24-48 hours.' });
      setForm({ name: '', company: '', email: '', phone: '', city: '', category: '', message: '' });
    } catch (err) {
      console.error('Dealer form error:', err);
      toast({ title: 'Error', description: 'Failed to submit. Please try again or email sales@harpalyk.com', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header scrolled={scrolled} />
      <main>
        <section className="bg-[#0A0A0A] pt-36 pb-20 px-6 lg:px-12 relative overflow-hidden">
          <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.4) 1px, transparent 1px)', backgroundSize: '48px 48px' }} />
          <div className="relative max-w-7xl mx-auto text-center">
            <p className="text-xs font-semibold tracking-[0.25em] text-emerald-400 uppercase mb-4">Dealer Program</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white mb-6">
              Become a Harpalyk Partner
            </h1>
            <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto">
              Bring premium home robotics to your city. High margins, complete training and a brand customers trust.
            </p>
          </div>
        </section>

        <section className="py-16 lg:py-24 px-6 lg:px-12 bg-[#F8F9FA]">
          <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-gray-900 mb-8">Dealer Benefits</h2>
              <div className="grid sm:grid-cols-2 gap-5" data-testid="dealer-benefits-grid">
                {BENEFITS.map(({ icon: Icon, title, desc }) => (
                  <div key={title} className="rounded-2xl border border-black/5 bg-white p-6 hover:shadow-lg hover:-translate-y-0.5 transition-[box-shadow,transform] duration-300">
                    <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                      <Icon size={20} strokeWidth={1.5} />
                    </div>
                    <h3 className="font-heading text-base font-semibold text-gray-900 mb-1.5">{title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-3xl border border-black/5 bg-white p-8 lg:p-10 shadow-sm h-fit">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-gray-900 mb-2">Register Your Interest</h2>
              <p className="text-sm text-gray-500 mb-8">Fill the form and our partnerships team will reach out within 24–48 hours.</p>
              <form onSubmit={submit} className="space-y-5" data-testid="dealer-registration-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Input name="name" placeholder="Your Name *" value={form.name} onChange={change} required className="h-12" data-testid="dealer-form-name" />
                  <Input name="company" placeholder="Company / Business Name *" value={form.company} onChange={change} required className="h-12" data-testid="dealer-form-company" />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Input type="email" name="email" placeholder="Email Address *" value={form.email} onChange={change} required className="h-12" data-testid="dealer-form-email" />
                  <Input type="tel" name="phone" placeholder="Phone Number *" value={form.phone} onChange={change} required className="h-12" data-testid="dealer-form-phone" />
                </div>
                <Input name="city" placeholder="City / Territory *" value={form.city} onChange={change} required className="h-12" data-testid="dealer-form-city" />
                <Select value={form.category || undefined} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="h-12" data-testid="dealer-form-category">
                    <SelectValue placeholder="Product Category of Interest *" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Floor Cleaning Robots">Floor Cleaning Robots</SelectItem>
                    <SelectItem value="Pool Cleaning Robots">Pool Cleaning Robots</SelectItem>
                    <SelectItem value="Window Cleaning Robots">Window Cleaning Robots</SelectItem>
                    <SelectItem value="Lawn Mowing Robots">Lawn Mowing Robots</SelectItem>
                    <SelectItem value="All Categories">All Categories</SelectItem>
                  </SelectContent>
                </Select>
                <Textarea name="message" placeholder="Tell us about your business (existing brands, showroom, experience)..." value={form.message} onChange={change} rows={4} className="resize-none" data-testid="dealer-form-message" />
                <Button type="submit" disabled={loading} data-testid="dealer-form-submit" className="w-full h-12 rounded-full bg-gray-900 text-white hover:bg-gray-700 text-base">
                  {loading ? 'Submitting...' : 'Submit Application'}
                  <Send size={16} className="ml-2" />
                </Button>
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppWidget />
    </div>
  );
}
